import React, { useState } from "react"

import { gql, useMutation } from "urql"

import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import { DocumentArrowDownIcon } from "@heroicons/react/24/solid"
import Tippy from "@tippyjs/react"

import { Color } from "~tailwindui/types/enums"

import { AlertMessageBox, Button, Spinner } from "~tailwindui/Basics"

import { RunMutation } from "src/types"

import LessonResourceCompletionDetails from "./LessonResourceCompletionDetails"

import "tippy.js/dist/tippy.css"

type LessonResourceCompletion = {
  id: string
  studentMastery: string
  createdAt: Date
  student: {
    id: string
    fullName: string
  }
}

type LessonResource = {
  id: string
  name: string
  file: {
    previewUrl: string
    thumbUrl: string
    downloadUrl: string
    filename: string
  }
  lessonResourceCompletions: LessonResourceCompletion[]
  sortOrder: number
}

export type LessonResourceDetailsProps = {
  airtutorsSessionId: string
  lessonResource: LessonResource
  isCurrent: boolean
}

const LessonResourceDetails: React.FC<LessonResourceDetailsProps> = ({
  airtutorsSessionId,
  lessonResource,
  isCurrent,
}) => {
  const [collapsed, setCollapsed] = useState(true)
  const [error, setError] = useState<string | null>()
  const [completeResourceState, completeResource]: [any, RunMutation] =
    useMutation(completeResourceMutation)

  return (
    <div key={lessonResource.id} className="ml-2 py-2">
      <span className="font-medium">{lessonResource.name}</span>
      <div className="my-1 flex flex-col gap-4 sm:flex-row">
        <a
          href={lessonResource.file.downloadUrl}
          className="flex cursor-pointer items-center gap-2 font-medium text-sky-700 hover:font-bold"
        >
          <DocumentArrowDownIcon className="h-5 w-5" />
          <span>Download {lessonResource.file.filename}</span>
        </a>

        <Tippy
          content={
            <img
              src={lessonResource.file.previewUrl}
              alt={lessonResource.name}
              className="mh-[80vh]"
            />
          }
          placement="auto"
          maxWidth="90vw"
        >
          <div>
            <img src={lessonResource.file.thumbUrl} alt={lessonResource.name} />
          </div>
        </Tippy>
      </div>

      {isCurrent && (
        <div className="my-2">
          <Button
            color={Color.Teal}
            onClick={() =>
              completeResource({
                airtutorsSessionId,
                lessonResourceId: lessonResource.id,
              }).then(result => {
                const { failures } = result.data.completeLessonResource
                if (failures.length > 0) {
                  setError(failures[0].message)
                }
              })
            }
            disabled={completeResourceState.fetching}
          >
            <div className="flex justify-center">
              Mark Worksheet Completed
              {completeResourceState.fetching && (
                <div className="ml-3 inline-block">
                  <Spinner />
                </div>
              )}
            </div>
          </Button>
          {error && <AlertMessageBox>{error}</AlertMessageBox>}
        </div>
      )}

      {lessonResource.lessonResourceCompletions.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setCollapsed(c => !c)}
            className="my-4 flex items-center gap-2 text-sm uppercase text-gray-500 hover:text-gray-900 hover:text-gray-900 md:my-0"
          >
            {collapsed ? (
              <>
                View Student Completions <ChevronDownIcon className="h-5 w-5" />
              </>
            ) : (
              <>
                Hide Student Completions <ChevronUpIcon className="h-5 w-5" />
              </>
            )}
          </button>
          {!collapsed && (
            <div className="mt-4 ml-2">
              {lessonResource.lessonResourceCompletions.map(completion => (
                <LessonResourceCompletionDetails
                  key={completion.id}
                  airtutorsSessionId={airtutorsSessionId}
                  lessonResourceCompletion={completion}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const completeResourceMutation = gql`
  mutation completeLessonResource(
    $airtutorsSessionId: ID!
    $lessonResourceId: ID!
  ) {
    completeLessonResource(
      airtutorsSessionId: $airtutorsSessionId
      lessonResourceId: $lessonResourceId
    ) {
      failures {
        message
      }
      airtutorsSession {
        lessonPlans {
          id
          name
        }
        currentLessonResources {
          id
          name
          sortOrder
          file {
            thumbUrl
            previewUrl
          }
          lessonTopic {
            id
            name
            sortOrder
          }
          lessonPlan {
            id
            name
          }
          lessonResourceCompletions(sessionId: $airtutorsSessionId) {
            id
            createdAt
            studentMastery
            student {
              id
              fullName
            }
          }
        }
      }
    }
  }
`

export default LessonResourceDetails
